import type { FeatureMap } from './specimenFeatures';

export type KeyOperator = 'equals' | 'not-equals' | 'greater-than' | 'less-than' | 'contains';
export interface KeyCondition { featureId: string; operator: KeyOperator; value: string | number | boolean; }
export interface KeyCoupletNode {
  id: string;
  type: 'couplet';
  statementYes: string;
  statementNo: string;
  condition: KeyCondition;
  yesNextId: string;
  noNextId: string;
}
export interface KeyTerminalNode { id: string; type: 'terminal'; specimenId: string; }
export type DichotomousKeyNode = KeyCoupletNode | KeyTerminalNode;
export interface DichotomousKey { id: string; title: string; rootNodeId: string; nodes: Record<string, DichotomousKeyNode>; }
export interface KeyTraversal {
  status: 'needs-observation' | 'identified' | 'invalid';
  path: string[];
  currentNode?: KeyCoupletNode;
  specimenId?: string;
  message: string;
}

function matchesCondition(observed: unknown, condition: KeyCondition): boolean | null {
  if (observed === undefined || observed === null) return null;
  switch (condition.operator) {
    case 'equals': return observed === condition.value;
    case 'not-equals': return observed !== condition.value;
    case 'greater-than': return typeof observed === 'number' && typeof condition.value === 'number' ? observed > condition.value : false;
    case 'less-than': return typeof observed === 'number' && typeof condition.value === 'number' ? observed < condition.value : false;
    case 'contains': return Array.isArray(observed) ? observed.includes(condition.value) : typeof observed === 'string' && typeof condition.value === 'string' ? observed.includes(condition.value) : false;
    default: return false;
  }
}

/** Traverses an authored key. A missing feature pauses at its couplet rather than guessing. */
export function traverseDichotomousKey(key: DichotomousKey, observations: FeatureMap): KeyTraversal {
  let nodeId = key.rootNodeId;
  const path: string[] = [];
  const visited = new Set<string>();
  while (true) {
    if (visited.has(nodeId)) return { status: 'invalid', path, message: 'This key contains a loop. Ask your teacher to review it.' };
    visited.add(nodeId);
    const node = key.nodes[nodeId];
    if (!node) return { status: 'invalid', path, message: `Key node ${nodeId} is missing.` };
    path.push(nodeId);
    if (node.type === 'terminal') return { status: 'identified', path, specimenId: node.specimenId, message: 'The key leads to this specimen. Check the identification against your recorded observations.' };
    const observed = observations[node.condition.featureId];
    const match = matchesCondition(observed, node.condition);
    if (match === null) return { status: 'needs-observation', path, currentNode: node, message: `Observe “${node.condition.featureId}” before choosing between the two statements.` };
    nodeId = match ? node.yesNextId : node.noNextId;
  }
}
